import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../../configuration";
import {
  CreateAgendaItemResourceDto,
  FetchError,
  GetAgendaItemResourcesDto,
  queryKeys,
  useAccessToken,
} from "../../..";
import { optimisticallyUpdateQueryData } from "../../utilities";
import { useSignalR } from "../../../../hooks";

type Variables = {
  agendaItemGuid: string;
  resourceGuid: string;
  title: string;
  url: string;
  isEmbedded: boolean;
  sortOrder: string;
};

const useCreateAgendaItemResourceRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createAgendaItemResourceRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: CreateAgendaItemResourceDto = {
      agendaItemGuid: variables.agendaItemGuid,
      resourceGuid: variables.resourceGuid,
      title: variables.title,
      url: variables.url,
      isEmbedded: variables.isEmbedded,
      sortOrder: variables.sortOrder,
    };

    const response = await fetch(`${configuration.apiRootUrl}/agenda-item-resources`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { createAgendaItemResourceRequest };
};

const useCreateAgendaItemResource = () => {
  const { createAgendaItemResourceRequest } = useCreateAgendaItemResourceRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: createAgendaItemResourceRequest,
    onMutate: async (variables) => {
      let cachedResource = await optimisticallyUpdateQueryData<GetAgendaItemResourcesDto>(
        queryClient,
        queryKeys.meetings.getAgendaItemResources(variables.agendaItemGuid),
        (cachedData) => ({
          resources: [
            ...cachedData.resources,
            {
              guid: variables.resourceGuid,
              title: variables.title,
              url: variables.url,
              isEmbedded: variables.isEmbedded,
              sortOrder: variables.sortOrder,
            },
          ],
        })
      );

      return { cachedResource };
    },
    onSuccess: async (_, variables) => {
      signalR.sendRefetchAgendaItemContent(variables.agendaItemGuid);
    },
    onError: async (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.meetings.getAgendaItemResources(variables.agendaItemGuid),
        context?.cachedResource
      );
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.meetings.getAgendaItemResources(variables.agendaItemGuid),
      });
    },
  });
};

export { useCreateAgendaItemResource };
